import { useState } from 'react';
import { Card, Typography, Form, Input, InputNumber, Checkbox, Button, Table, Tag, Space, Alert, Popconfirm, Spin, Flex, message } from 'antd';
import { CopyOutlined, MailOutlined, LinkOutlined } from '@ant-design/icons';
import { useNodeInvites } from '../../hooks/use_node_invites';
import { useNodeInfo } from '../../hooks/use_node_info';

const { Title, Text } = Typography;

const STATUS_COLOR = { open: 'green', used: 'default', expired: 'orange', revoked: 'red' };

const copy = url => {
    navigator.clipboard?.writeText(url)
        .then(() => message.success('Link copied'))
        .catch(() => message.error('Could not copy the link'));
};

const when = iso => (iso ? new Date(iso).toLocaleDateString() : '—');

/**
 * Superadmin-only: invite people to this node (Node settings → Invites). An
 * invite is to an e-mail address — only an account signed in with it can
 * accept, once — or a link anyone may use. Either way the person ends up a
 * member here: an approved profile and a person entity, with no approval
 * step to wait for. The invite shows up in their node switcher wherever
 * they are signed in, and the link works on its own.
 *
 * The e-mail uses the wording in the Invite email tab.
 */
function NodeInvites() {
    const { invites, loading, error, create, revoke } = useNodeInvites();
    const { nodeName } = useNodeInfo();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [made, setMade] = useState(null); // the invite just issued, link in hand
    const email = Form.useWatch('email', form);

    const submit = values => {
        setBusy(true);
        setMade(null);
        const address = (values.email || '').trim();
        create({
            email: address || null,
            name: (values.name || '').trim() || null,
            expires_days: values.expires_days || 14,
            send_email: !!address && values.send_email !== false,
        })
            .then(invite => {
                setMade(invite);
                form.resetFields();
                message.success(invite.email_sent ? `Invite e-mailed to ${invite.email}` : 'Invite created');
            })
            .catch(err => message.error(err?.message || 'Could not create the invite'))
            .finally(() => setBusy(false));
    };

    const columns = [
        {
            title: 'Who',
            key: 'who',
            render: (_, i) => i.email
                ? <Space size={4}><MailOutlined /><span>{i.name ? `${i.name} <${i.email}>` : i.email}</span></Space>
                : <Space size={4}><LinkOutlined /><span>Anyone with the link</span></Space>,
        },
        { title: 'Status', dataIndex: 'status', key: 'status', render: s => <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag> },
        { title: 'Used', key: 'used', render: (_, i) => `${i.use_count}${i.max_uses ? ` of ${i.max_uses}` : ''}` },
        { title: 'Expires', dataIndex: 'expires_at', key: 'expires', render: when },
        { title: 'Sent by', dataIndex: 'invited_by_name', key: 'by', render: n => n || '—' },
        {
            title: '',
            key: 'actions',
            render: (_, i) => i.status === 'open' && (
                <Space size={4}>
                    <Button size="small" icon={<CopyOutlined />} onClick={() => copy(i.url)}>Copy link</Button>
                    <Popconfirm title="Revoke this invite?" okText="Revoke" onConfirm={() => revoke(i.id).catch(() => message.error('Could not revoke the invite'))}>
                        <Button size="small" danger>Revoke</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card style={{ marginTop: 16 }}>
            <Title level={5} style={{ margin: 0 }}>Invites</Title>
            <div>
                <Text type="secondary">
                    Invite someone to {nodeName || 'this node'}. With an e-mail address only that person can accept,
                    once; without one you get a link anyone may use. Whoever accepts becomes a member straight away,
                    and the invite shows up in their node menu wherever they are signed in.
                </Text>
            </div>

            <Form form={form} layout="vertical" onFinish={submit} style={{ marginTop: 16 }} initialValues={{ expires_days: 14, send_email: true }} requiredMark={false}>
                <Flex gap={12} wrap>
                    <Form.Item name="email" label="E-mail" style={{ flex: '1 1 220px', marginBottom: 8 }} rules={[{ type: 'email', message: 'Not an e-mail address' }]}>
                        <Input placeholder="pat@example.com (optional)" maxLength={255} />
                    </Form.Item>
                    <Form.Item name="name" label="Name" style={{ flex: '1 1 160px', marginBottom: 8 }}>
                        <Input placeholder="Pat (optional)" maxLength={255} />
                    </Form.Item>
                    <Form.Item name="expires_days" label="Valid for (days)" style={{ flex: '0 0 130px', marginBottom: 8 }}>
                        <InputNumber min={1} max={365} style={{ width: '100%' }} />
                    </Form.Item>
                </Flex>
                <Flex gap={12} align="center" wrap>
                    <Form.Item name="send_email" valuePropName="checked" style={{ marginBottom: 0 }}>
                        <Checkbox disabled={!email}>Send the invite by e-mail</Checkbox>
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={busy}>
                        {email ? 'Invite' : 'Create link'}
                    </Button>
                </Flex>
            </Form>

            {made && (
                <Alert
                    style={{ marginTop: 16 }}
                    type="success"
                    showIcon
                    title={made.email ? `Invite for ${made.email} ${made.email_sent ? 'sent' : 'created (e-mail not sent)'}` : 'Invite link created'}
                    description={
                        <Space.Compact style={{ width: '100%' }}>
                            <Input readOnly value={made.url} />
                            <Button icon={<CopyOutlined />} onClick={() => copy(made.url)}>Copy</Button>
                        </Space.Compact>
                    }
                    closable
                    onClose={() => setMade(null)}
                />
            )}

            {error && error.status !== 403 && (
                <Alert style={{ marginTop: 16 }} type="error" showIcon title="Could not load this node's invites" />
            )}
            {loading ? (
                <Spin style={{ display: 'block', marginTop: 16 }} />
            ) : (
                <Table
                    style={{ marginTop: 16 }}
                    size="small"
                    rowKey="id"
                    columns={columns}
                    dataSource={invites}
                    pagination={invites.length > 20 ? { pageSize: 20 } : false}
                    locale={{ emptyText: 'No invites yet' }}
                />
            )}
        </Card>
    );
}

export default NodeInvites;
